import { useContext, useEffect } from 'react';
import _ from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { OverviewColumns } from '../config/columnConfig';
import { useOutletContext, useParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useGetSerpDataQuery } from '@/app/Serp/api/getSerpDataApi';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';
import { useDispatch, useSelector } from 'react-redux';
import { setSerp } from '@/app/Serp/reducers/serpDataSlice';
import { useGetStatusSerpQuery } from '@/app/Serp/api/historyApi';
import NoSerpData from '../components/common/NoSerpData';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import CircularChartCard from '../components/CircularChartCard';
import WordSearchVolume from '../components/WordSearchVolume';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import DoughnutCard from '../components/DoughnutCard';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import CardChartCircleLoading from '../../common/components/skeleton/CardChartCircleLoading';
import CardChartSemiCircleLoading from '../../common/components/skeleton/CardChartSemiCircleLoading';
import CardBarChartLoading from '../../common/components/skeleton/CardBarChartLoading';
import { NewSerpContext } from '@/context/Context';
import useDomain from '@/hooks/useDomain';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import type { SerpDataRootState } from '@/app/Serp/types/serpApiTypes';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

const Overview = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const dispatch = useDispatch();
  const serpData = useSelector((state: SerpDataRootState) => state.serpData);
  const { step } = useContext(NewSerpContext);
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const { slug } = useParams<{ slug?: string }>();
  const { domain_slug } = useDomain();
  const { data: userInfo } = useGetUserInfoQuery();
  const serp_id = slug === 'last' ? 'last' : slug;

  useGetStatusSerpQuery({
    serp_id: serp_id || 'last',
    domain_slug,
    requested_user: {
      name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
      email: userInfo?.data?.email
    }
  });

  const { data, isLoading, isError, error, refetch, isFetching } =
    useGetSerpDataQuery(
      {
        serp_id: serp_id ?? 'last',
        domain_slug,
        requested_user: {
          name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
          email: userInfo?.data?.email
        }
      },
      {
        skip: step !== 1
      }
    );

  useEffect(() => {
    setBreadcrumbData([{ text: 'نمای کلی', href: '#' }]);
  }, []);

  useEffect(() => {
    if (!data) return;
    dispatch(setSerp(data.data ?? null));
  }, [data]);

  const colorMaker = (index: number) => {
    if (index % 5 === 0) {
      return getColor('primary');
    } else if (index % 4 === 0) {
      return 'info';
    } else if (index % 3 === 0) {
      return 'success';
    } else if (index % 2 === 0) {
      return 'warning';
    } else if (index % 1 === 0) {
      return 'danger';
    } else {
      return 'warning';
    }
  };

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }

  if (!isLoading && Object.keys(serpData).length == 0) return <NoSerpData />;

  return (
    <>
      <Row className="g-3 mb-3">
        <Col xl={5} md={12} style={{ height: 312 }}>
          {!isLoading && serpData?.keyword_difficulty ? (
            <DoughnutCard diff={serpData.keyword_difficulty} />
          ) : (
            <CardChartSemiCircleLoading
              title="میزان سختی کلمه"
              tooltip={serpTooltips.wordDiff}
            />
          )}
        </Col>

        <Col xl={7} md={12}>
          {!isLoading ? (
            <WordSearchVolume data={serpData.search_volumes} />
          ) : (
            <CardBarChartLoading
              limit={30}
              width={10}
              height={15}
              margin={38}
              marginBetweenLine={5}
              showFooter={false}
            />
          )}
        </Col>
      </Row>
      {!isLoading ? (
        <Row className="g-3 mb-3">
          <Col xxl={4} md={6}>
            {(serpData.analysis_serp?.results_types ?? []).length ? (
              <CircularChartCard
                title={<> توزیع ویژگی نتایج</>}
                tooltip={serpTooltips.characteristicDistributionOfResults}
                footer={
                  <>
                    در نتایج <b>تبلیغات</b>{' '}
                    {(serpData.analysis_serp?.results_types ?? []).filter(
                      item => item.name == 'تبلیغات'
                    ).length != 0 ? (
                      <>وجود دارد</>
                    ) : (
                      <>وجود ندارد</>
                    )}{' '}
                    و <b> فیچر اسنیپت</b>{' '}
                    {(serpData.analysis_serp?.results_types ?? []).filter(
                      item => item.name == 'فیچر اسنیپت'
                    ).length != 0 ? (
                      <>وجود دارد</>
                    ) : (
                      <>وجود ندارد</>
                    )}
                  </>
                }
                data={(serpData.analysis_serp?.results_types ?? []).map(
                  (item, i) => ({
                    color: colorMaker(i),
                    count: item.y,
                    percentage: item.y,
                    name: item.name
                  })
                )}
              />
            ) : (
              <CardChartCircleLoading
                title={<> توزیع ویژگی نتایج</>}
                tooltip={serpTooltips.characteristicDistributionOfResults}
              />
            )}
          </Col>
          <Col xxl={4} md={6}>
            {serpData?.analysis_domains &&
            (serpData?.analysis_domains?.num_ir_host ||
              serpData?.analysis_domains?.num_non_ir_host) ? (
              <CircularChartCard
                title={<>توزیع مکان هاست</>}
                tooltip={serpTooltips.distributionOfHostLocation}
                footer={
                  <>
                    {(serpData.analysis_domains.num_ir_host ?? 0) >
                    (serpData.analysis_domains.num_non_ir_host ?? 0) ? (
                      <>
                        <b>
                          {' '}
                          {Math.round(
                            ((serpData.analysis_domains.num_ir_host ?? 0) *
                              100) /
                              ((serpData.analysis_domains.num_ir_host ?? 0) +
                                (serpData.analysis_domains.num_non_ir_host ??
                                  0))
                          )}{' '}
                        </b>
                        درصد سایت ها از هاست داخل ایران استفاده میکنند.
                      </>
                    ) : (
                      <>
                        <b>
                          {' '}
                          {Math.round(
                            ((serpData.analysis_domains.num_non_ir_host ??
                              0) *
                              100) /
                              ((serpData.analysis_domains.num_ir_host ?? 0) +
                                (serpData.analysis_domains.num_non_ir_host ??
                                  0))
                          )}{' '}
                        </b>
                        درصد سایت ها از هاست خارج ایران استفاده میکنند.
                      </>
                    )}
                  </>
                }
                data={[
                  {
                    color: getColor('primary'),
                    percentage: serpData.analysis_domains.num_ir_host,
                    count: serpData.analysis_domains.num_ir_host ?? 0,
                    name: 'داخل ایران'
                  },
                  {
                    color: 'info',
                    percentage: serpData.analysis_domains.num_non_ir_host,
                    count: serpData.analysis_domains.num_non_ir_host ?? 0,
                    name: 'خارج ایران'
                  }
                ]}
              />
            ) : (
              <CardChartCircleLoading
                title={<>توزیع مکان هاست</>}
                tooltip={serpTooltips.distributionOfHostLocation}
              />
            )}
          </Col>
          <Col xxl={4} md={12}>
            {serpData?.analysis_domains?.domains_suffix ? (
              <CircularChartCard
                title={<>توزیع پسوند دامنه</>}
                tooltip={serpTooltips.distributionOfDomainSuffix}
                footer={
                  <>
                    اکثر سایت ها دارای دامنه با پسوند{' '}
                    <b>
                      {
                        _.maxBy(
                          serpData.analysis_domains.domains_suffix,
                          item => item.y
                        )?.name
                      }{' '}
                    </b>
                    هستند.
                  </>
                }
                data={serpData.analysis_domains.domains_suffix.map(
                  (item, i) => ({
                    color: colorMaker(i),
                    count: item.y,
                    percentage: item.y,
                    name: item.name
                  })
                )}
              />
            ) : (
              <CardChartCircleLoading
                title={<>توزیع پسوند دامنه</>}
                tooltip={serpTooltips.distributionOfDomainSuffix}
              />
            )}
          </Col>
        </Row>
      ) : (
        <Row className="g-3 mb-3">
          <Col xxl={4} md={6} style={{ height: 413 }}>
            <CardChartCircleLoading />
          </Col>
          <Col xxl={4} md={6} style={{ height: 413 }}>
            <CardChartCircleLoading />
          </Col>
          <Col xxl={4} md={6} style={{ height: 413 }}>
            <CardChartCircleLoading />
          </Col>
        </Row>
      )}
      <Row className="g-3 mb-3">
        <Col>
          {!isLoading ? (
            <AgGridWrapper
              height="526px"
              columns={OverviewColumns(isMdUp)}
              data={serpData.items}
            />
          ) : (
            <AgGridSkeleton height="576px" />
          )}
        </Col>
      </Row>
    </>
  );
};

export default Overview;
